import React, { useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { HiArrowLeft } from 'react-icons/hi';
import { includes, each, omit } from 'lodash';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import Button from 'partial/components/Button';
import FormInput from 'partial/form/FormInput';
import FormToggle from 'partial/form/FormToggle';
import Form, { ErrorLabel } from 'partial/form/Form';
import FormPassword from 'partial/form/FormPassword';
import { useCreateModal } from 'partial/components/Modal';
import FormReactSelect from 'partial/form/FormReactSelect';

import {
  useGetBillerOptions,
  useCreateACLUser,
  useUpdateACLUser,
  useGetRoleOptions,
  useChangePassword,
} from '../hooks';
import * as c from '../constants';
import ChangePasswordModal from './ChangePasswordModal';

const initialValues = (initValue, isEdit) => ({
  first_name: initValue?.first_name || '',
  last_name: initValue?.last_name || '',
  email_address: initValue?.email || '',
  role: initValue?.role_id || '',
  biller: initValue?.biller_id || '',
  status: isEdit ? initValue?.status === '1' || false : true,
  password: '',
  password_confirmation: '',
});

function AddEditAclUser({ data }) {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [changePassword, isChangePasswordLoading] = useChangePassword();
  const modal = useCreateModal('CHANGE_PASSWORD', { isChangePasswordLoading });
  const isEdit = includes(location.pathname, 'edit');
  const [selectedRole, setSelectedRole] = useState(
    location.state?.initValues?.role || ''
  );
  const [submit, isSubmitLoading] = useCreateACLUser();
  const [update, isUpdateLoading] = useUpdateACLUser();
  const [roleOptions, isRoleOptionsLoading] = useGetRoleOptions();
  const [billerOptions, isBillerOptionsLoading] = useGetBillerOptions();
  const [validateOnChange, setValidateOnChange] = React.useState(false);

  const initValue = React.useMemo(() => location.state?.initValues || data, [
    location,
    data,
  ]);

  const { values, setFieldValue, handleSubmit, errors } = useFormik({
    initialValues: initialValues(initValue, isEdit),
    validationSchema: isEdit
      ? c.UPDATE_VALIDATION_SCHEMA
      : c.CREATE_VALIDATION_SCHEMA,
    onSubmit: (form, { setFieldError }) => {
      const args = {
        first_name: form?.first_name,
        last_name: form?.last_name,
        email_address: form?.email_address,
        password: form?.password,
        password_confirmation: form?.password_confirmation,
        role: form?.role,
        biller: form?.biller,
        status: form?.status ? '1' : '0',
        is_mfa_enabled: '0',
      };

      if (isEdit) {
        update(
          id,
          selectedRole === 'Biller User'
            ? omit(args, ['password', 'password_confirmation'])
            : omit(args, ['biller', 'password', 'password_confirmation']),
          () => history.push('/acl/users'),
          ({ response }) => {
            each(response?.errors, (v, k) => {
              setFieldError(k, v);
            });
          }
        );
      } else {
        submit(
          selectedRole === 'Biller User' ? args : omit(args, 'biller'),
          () => history.push('/acl/users'),
          ({ response }) => {
            each(response?.errors, (v, k) => {
              setFieldError(k, v);
            });
          }
        );
      }
      setValidateOnChange(true);
    },
    validateOnChange,
  });

  const handleOpenChangePasswordModal = () => {
    modal({
      // eslint-disable-next-line
      content: (onClose) => ({ isChangePasswordLoading: isLoadingProp }) => (
        <ChangePasswordModal
          id={id}
          submit={changePassword}
          isLoading={isLoadingProp}
          onClose={onClose}
        />
      ),
    });
  };

  return (
    <div className="flex flex-col h-full p-6">
      <div>
        <Button
          type="button"
          icon={HiArrowLeft}
          onClick={() => history.goBack()}
        >
          Back
        </Button>
      </div>
      <div className="flex items-center justify-between border-b py-6">
        <h1 className="title-h1">
          {isEdit
            ? `${initValue?.first_name || '---'} ${
                initValue?.last_name || '---'
              } > Edit Member`
            : 'Add New Member'}
        </h1>
        {isEdit ? (
          <Button onClick={handleOpenChangePasswordModal} outlinePrimary>
            Change Password
          </Button>
        ) : null}
      </div>
      <div className="flex flex-col h-0 flex-grow overflow-y-auto">
        <Form
          className="grid grid-cols-5 gap-6 mt-6 ml-px"
          onSubmit={handleSubmit}
          error={errors}
        >
          <div className="col-span-2">
            <FormInput
              label="First Name"
              name="first_name"
              onSetFieldValue={setFieldValue}
              value={values?.first_name || ''}
              error={errors?.first_name || ''}
              required
            />
            <ErrorLabel name="first_name" />
          </div>
          <div className="col-span-2">
            <FormInput
              label="Last Name"
              name="last_name"
              onSetFieldValue={setFieldValue}
              value={values?.last_name || ''}
              error={errors?.last_name || ''}
              required
            />
            <ErrorLabel name="last_name" />
          </div>
          <div className="flex flex-col space-y-3">
            <span className="block font-medium mb-2 text-gray-500">Status</span>
            <div className="flex items-center">
              <div
                className={cn(
                  'text-base w-20',
                  values?.status ? 'text-secondary-600' : 'text-danger-600'
                )}
              >
                {values?.status ? 'Active' : 'Inactive'}
              </div>
              <FormToggle
                label=""
                secondary
                name="status"
                onSetFieldValue={setFieldValue}
                checked={values?.status}
              />
            </div>
          </div>
          <div className="col-span-2">
            <FormInput
              label="Email"
              name="email_address"
              onSetFieldValue={setFieldValue}
              value={values?.email_address || ''}
              error={errors?.email_address || ''}
              required
            />
            <ErrorLabel name="email_address" />
          </div>
          <div className="col-span-2">
            <FormReactSelect
              name="role"
              label="Role"
              onSetFieldValue={setFieldValue}
              onChangeCallback={(value) => setSelectedRole(value?.label)}
              options={roleOptions}
              isLoading={isRoleOptionsLoading}
              value={values?.role || ''}
              error={errors?.role || ''}
              required
            />
            <ErrorLabel name="role" />
          </div>
          {selectedRole === 'Biller User' ? (
            <div className="col-span-4">
              <FormReactSelect
                label="Biller Name"
                name="biller"
                options={billerOptions}
                isLoading={isBillerOptionsLoading}
                onSetFieldValue={setFieldValue}
                value={values?.biller || ''}
                error={errors?.biller || ''}
                isSearchable
                required
              />
              <ErrorLabel name="biller" />
            </div>
          ) : null}
          {!isEdit ? (
            <>
              <div className="col-span-2">
                <FormPassword
                  label="Password"
                  name="password"
                  onSetFieldValue={setFieldValue}
                  value={values?.password || ''}
                  error={errors?.password || ''}
                  required
                />
                <ErrorLabel name="password" />
              </div>
              <div className="col-span-2">
                <FormPassword
                  label="Confirm Password"
                  name="password_confirmation"
                  onSetFieldValue={setFieldValue}
                  value={values?.password_confirmation || ''}
                  error={errors?.password_confirmation || ''}
                  required
                />
                <ErrorLabel name="password_confirmation" />
              </div>
            </>
          ) : null}
          <div className="col-span-2 flex space-x-4">
            <Button
              type="button"
              onClick={() => history.push('/acl/users')}
              isLoading={isSubmitLoading || isUpdateLoading}
              outlinePrimary
            >
              Cancel
            </Button>
            <Button
              type="submit"
              primary
              isLoading={isSubmitLoading || isUpdateLoading}
            >
              {isEdit ? 'Save Changes' : 'Create New Member'}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}

AddEditAclUser.propTypes = {
  data: PropTypes.instanceOf(Object),
};

AddEditAclUser.defaultProps = {
  data: {},
};

export default AddEditAclUser;
